<script setup lang="ts">
import { watch, computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import DataTable from 'primevue/datatable';
import useVuelidate from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { PersonStores, useRelayHubStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS, MEDICAID_FEATUREABLE_TYPE } from '@/enum';
import { MedicaidEnabledOrganizationInterface } from '@/types';

const profileStore = PersonStores.useUserProfileStore();
const relayHubStore = useRelayHubStore();

const {
  medicaidEnabledOrganizations,
  selectedMedicaidOrganizationsId,
  originalMedicaidOrganizationsId,
  medicaidBilling,
  originalMedicaidEnabledOrganizations,
} = storeToRefs(profileStore);

const toggleMedicaidEnabled = ref<boolean>(medicaidBilling?.value?.medicaid_enabled || false);

const isToggledOn = (v: string, vm: MedicaidEnabledOrganizationInterface) =>
  selectedMedicaidOrganizationsId.value.find((id) => id === vm.id);

const rules = computed(() => {
  return {
    medicaidEnabledOrganizations: {
      $each: helpers.forEach({
        NPI: {
          requiredIfCustom: helpers.withMessage(
            'NPI is required if the organization is selected',
            requiredIf(isToggledOn),
          ),
        },
      }),
    },
  };
});

const v$ = useVuelidate(rules, { medicaidEnabledOrganizations });

const onSave = async () => {
  if (!v$.value.$error) {
    if (isMedicaidOrganizationsChanged()) {
      await profileStore.saveMedicaidBilling();
      profileStore.doToast('Medicaid Billing has been updated');
    }
    if (toggleMedicaidEnabled.value !== medicaidBilling?.value?.medicaid_enabled) {
      await saveMedicaidEnable();
    }
    await getBillingInfo();
    resetDirty();
  }
};

const resetDirty = () => {
  v$.value.$reset();
};

const onCancel = () => {
  resetDirty();
  getBillingInfo();
};

watch(
  () => profileStore.id,
  (newId) => {
    if (newId) {
      getBillingInfo();
    }
  },
);

const getBillingInfo = async () => {
  await profileStore.getMedicaidBilling();
  if (profileStore.error) {
    profileStore.doToast(profileStore.error as string, 'Error', true);
  }
};

const notChecked = (id: number) => {
  return !selectedMedicaidOrganizationsId.value.includes(id);
};

const resetRow = (item: MedicaidEnabledOrganizationInterface) => {
  if (notChecked(item.id)) {
    item.NPI = '';
  }
  v$.value.medicaidEnabledOrganizations.$touch();
};

const saveMedicaidEnable = async () => {
  const personId: number = profileStore.id as number;

  if (toggleMedicaidEnabled.value) {
    if (selectedMedicaidOrganizationsId.value.length > 0) {
      await relayHubStore.enableMedicaid([personId], MEDICAID_FEATUREABLE_TYPE.PEOPLE);

      if (!relayHubStore.error) relayHubStore.doToast(`Service Provider is now Medicaid Enabled.`);
      else relayHubStore.doToast(relayHubStore.message as string, 'Error', true);
    } else {
      toggleMedicaidEnabled.value = false;
      relayHubStore.doToast(
        'At least one Medicaid Organization must be selected to enable Medicaid Billing.',
        'Error',
        true,
      );
    }
  } else {
    await relayHubStore.disableMedicaid([personId], MEDICAID_FEATUREABLE_TYPE.PEOPLE);
    if (!relayHubStore.error) relayHubStore.doToast(`Service Provider is now Medicaid Disabled.`);
    else relayHubStore.doToast(relayHubStore.message as string, 'Error', true);
  }

  await getBillingInfo();
};

/**
 * Deep compares two arrays of MedicaidEnabledOrganizationInterface objects.
 * Returns true if any difference is found, false otherwise.
 */
const isMedicaidOrganizationsChanged = (): boolean => {
  const original = originalMedicaidEnabledOrganizations.value;
  const current = medicaidEnabledOrganizations.value;
  const originalIds = originalMedicaidOrganizationsId.value;
  const currentIds = selectedMedicaidOrganizationsId.value;

  // Compare arrays of IDs
  if (originalIds.length !== currentIds.length) return true;

  const idDiff = originalIds.find((id, i) => id !== currentIds[i]);
  if (idDiff !== undefined) return true;

  // Compare organization objects
  if (original.length !== current.length) return true;
  for (let i = 0; i < original.length; i++) {
    const orig = original[i];
    const curr = current[i];
    for (const key in orig) {
      if (Object.prototype.hasOwnProperty.call(orig, key)) {
        if (orig[key] !== curr[key]) return true;
      }
    }
  }
  return false;
};
</script>

<template>
  <FormCard
    title="Medicaid Billing"
    :dirty="v$.$anyDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="!can([PERMISSIONS.PEOPLE_UPDATE]) || v$.$error"
  >
    <template #form>
      <form class="w-full">
        <div v-if="medicaidEnabledOrganizations.length" class="flex flex-wrap align-content-center mb-3 gap-3 pl-6">
          <Checkbox id="toggleMedicaidEnabled" v-model="toggleMedicaidEnabled" :binary="true" @change="v$.$touch" />
          <label for="toggleMedicaidEnabled" class="mr-3">Enable</label>
        </div>
        <div v-if="!medicaidEnabledOrganizations.length" class="text-center p-3 pb-0">
          No Medicaid Billing Organizations found.
        </div>
        <DataTable
          v-else
          :scrollable="true"
          :value="medicaidEnabledOrganizations"
          responsive-layout="scroll"
          class="w-full"
          scroll-height="flex"
        >
          <Column header="Eligible" class="text-center justify-content-center px-3" :style="{ maxWidth: '150px' }">
            <template #body="slotProps">
              <Checkbox
                v-model="selectedMedicaidOrganizationsId"
                :name="slotProps.data.name"
                :value="slotProps.data.id"
                @change="resetRow(slotProps.data)"
              />
            </template>
          </Column>
          <Column field="name" header="Organization Name" />
          <Column field="NPI" header="Supervisor/NPI Code">
            <template #body="slotProps">
              <div class="flex flex-column w-full">
                <InputText v-model="slotProps.data.NPI" :disabled="notChecked(slotProps.data.id)" @input="v$.$touch" />
                <div v-if="v$.medicaidEnabledOrganizations.$dirty">
                  <div
                    v-for="error of v$.medicaidEnabledOrganizations.$each.$response.$errors[slotProps.index].NPI"
                    :key="error.$uid"
                    class="input-errors"
                  >
                    <small>{{ error.$message }}</small>
                  </div>
                </div>
              </div>
            </template>
          </Column>
        </DataTable>
      </form>
    </template>
  </FormCard>
</template>
